// La liste des personnes qui ont réagi à une publication.
//
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE
//
// On lisait « 12 amens » sans jamais savoir de qui. Demandé par Josaphat le
// 18 août 2026 : « la liste des personnes qui ont réagi, comme sur Facebook ».
//
// ⚠️ ELLE N'A ÉTÉ POSSIBLE QU'À PARTIR D'UN SECOND RANGEMENT.
// Une réaction s'écrivait dans `users/{uid}/amens/{postId}` — le tiroir
// personnel, qui répond à « qu'ai-je mis ? » et à rien d'autre. Pour savoir QUI
// a réagi à une publication, il aurait fallu ouvrir le tiroir de chaque membre.
// La même réaction s'écrit désormais AUSSI sous la publication, dans le même
// lot. Voir `store.js`, `amens.poser`.
//
// ⚠️ LES NOMS NE SONT PAS DANS CE TIROIR, ET C'EST DÉLIBÉRÉ. Chacun écrit
// lui-même dans son document de réaction : il aurait pu s'y donner le nom d'un
// autre, et cette liste serait devenue un endroit où se faire passer pour
// quelqu'un. On relit donc les fiches à l'ouverture du panneau — jamais avant :
// un fil de vingt cartes ne doit pas coûter vingt listes que personne n'a
// demandées.
//
// ⚠️ LE MÊME PANNEAU EXISTE SUR LE TÉLÉPHONE — `koinonia-native/composants/
// QuiAReagi.jsx`. Les deux se ressemblent volontairement : c'est la consigne de
// Josaphat, les deux applications doivent parler d'une seule voix.

import { useEffect, useState } from "react";
import Store from "../store.js";
import Avatar from "./Avatar.jsx";
import Certifie from "./Certifie.jsx";
import Icone from "./Icone.jsx";
import { reaction } from "../reactions.js";

export default function QuiAReagi({ ctx, postId, total = 0, ouvert, fermer }) {
  const [gens, setGens] = useState(null);   // null = on cherche encore
  const [refus, setRefus] = useState("");

  useEffect(() => {
    if (!ouvert) return;
    let vivant = true;
    setGens(null);
    setRefus("");
    (async () => {
      try {
        const brut = await Store.amens.qui(postId);
        if (!vivant) return;
        if (!brut.length) return setGens([]);
        /* `ctx.profil` garde ce qu'il a déjà lu : après quelques minutes dans
           l'application, la plupart des fiches ne coûtent plus rien. */
        const fiches = await Promise.all(
          brut.map((r) => (ctx?.profil ? ctx.profil(r.uid).catch(() => null) : null))
        );
        if (!vivant) return;
        setGens(brut.map((r, i) => ({ ...r, membre: fiches[i] || { uid: r.uid, nom: "Membre" } })));
      } catch (e) {
        if (vivant) { setRefus(e?.message || "Liste indisponible."); setGens([]); }
      }
    })();
    return () => { vivant = false; };
  }, [postId, ouvert, ctx]);

  // La touche Échap ferme, comme partout ailleurs sur le site.
  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (e) => e.key === "Escape" && fermer();
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [ouvert, fermer]);

  if (!ouvert) return null;

  return (
    <div className="voile-reactions" onClick={fermer} role="presentation">
      <div
        className="panneau-reactions"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Personnes qui ont réagi"
      >
        <header className="panneau-reactions__tete">
          {/* ⚠️ PAS DE NOMBRE DANS LE TITRE — même correction que sur le
              téléphone, le 20 août 2026. Il annonçait « 2 réactions » sous une
              carte qui en affichait 4 : le tiroir qui retient QUI a réagi
              n'existe que depuis le 18 août, et les réactions d'avant vivent
              dans le tiroir privé de chacun, illisible par autrui. Elles sont
              irrécupérables ; le titre ne doit donc pas prétendre les compter. */}
          <h2>Qui a réagi</h2>
          <button type="button" className="btn-icone" onClick={fermer} aria-label="Fermer">
            <Icone nom="fermer" taille={20} />
          </button>
        </header>

        {gens === null ? (
          <p className="petit texte-doux panneau-reactions__mot">Un instant…</p>
        ) : refus ? (
          <p className="petit texte-doux panneau-reactions__mot">{refus}</p>
        ) : gens.length === 0 ? (
          <p className="petit texte-doux panneau-reactions__mot">Personne n'a encore réagi.</p>
        ) : (
          <>
          {total > gens.length && (
            <p className="petit texte-doux panneau-reactions__note">
              {total - gens.length === 1
                ? "Une réaction plus ancienne n'est pas listée."
                : `${total - gens.length} réactions plus anciennes ne sont pas listées.`}
            </p>
          )}
          <ul className="panneau-reactions__liste">
            {gens.map((g) => (
              <li key={g.uid}>
                <a className="panneau-reactions__ligne" href={`#/profil/${g.uid}`} onClick={fermer}>
                  <span className="panneau-reactions__tete-avatar">
                    <Avatar membre={g.membre} taille={44} />
                    {/* La réaction se pose sur l'avatar, comme sur Facebook :
                        on lit d'un regard qui a mis quoi. */}
                    <span className="panneau-reactions__pastille" aria-hidden="true">
                      {reaction(g.type).emoji}
                    </span>
                  </span>
                  <span className="panneau-reactions__nom">
                    {g.membre?.nom || "Membre"}
                    <Certifie membre={g.membre} taille={14} />
                  </span>
                </a>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </div>
  );
}
